import React, {useContext, useEffect, useMemo, useReducer, useRef, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {Context} from '../index';
import OrderService from '../services/OrderService';
import MyButton from '../UI/MyButton/MyButton';
import Popup from "reactjs-popup";
import EntityList from "../components/EntityList";
import {toJS} from "mobx";
import {MaterialReactTable} from "material-react-table";
import {Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button} from '@mui/material';
import DemandService from "../services/DemandService";
import {observer} from 'mobx-react-lite';
import {useTranslation} from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';

const Start = observer(function Start() {
    const {t} = useTranslation();
    const {store} = useContext(Context);
    const [entities, setEntities] = useState([]);
    const [order, setOrder] = useState([]);
    const [testDialogOpen, setTestDialogOpen] = useState(false);
    const [testDemandId, setTestDemandId] = useState('');
    const [orderId, setOrderId] = useState('');
    const [showWaitingList, setShowWaitingList] = useState(false);
    const [showScanPopup, setShowScanPopup] = useState(false);
    const [showOrderNumberDialog, setShowOrderNumberDialog] = useState(false);
    const [orderNumber, setOrderNumber] = useState('');
    const showScanPopupRef = useRef();
    showScanPopupRef.current = showScanPopup;
    const [showOrderList, setShowOrderList] = useState(false);
    let [barcodeParts, setBarcodeParts] = useState('');
    const [barcode, setBarcode] = useState({});
    const [, forceUpdate] = useReducer(x => x + 1, 0);
    const navigate = useNavigate();
    const labelRef = useRef(null);

    const pagination = {
        pageSize: 100
    }
    const columnsWithDemand = useMemo(
        () => [
            {
                accessorKey: 'name',
                header: t('orders.shipment'),
                size: 200
            },
            {
                accessorKey: 'customerorderName',
                header: t('orders.order'),
                size: 200
            },
            {
                accessorFn: (row) => {
                    const RED_ZONE_ORDERS_DATE = new Date();

                    const orderDate = new Date(row.created);

                    const diffInMs = RED_ZONE_ORDERS_DATE - orderDate;
                    const diffInHours = diffInMs / (1000 * 60 * 60);
                    return diffInHours.toFixed(2);
                },
                id: 'created',
                header: t('orders.hoursInProgress'),
                size: 200
            }
        ],
        [t],
    );

    const columns = useMemo(
        () => [
            {
                accessorKey: 'name',
                header: t('orders.order'),
                size: 200
            },
            {
                accessorFn: (row) => {
                    const RED_ZONE_ORDERS_DATE = new Date();

                    const orderDate = new Date(row.created);

                    const diffInMs = RED_ZONE_ORDERS_DATE - orderDate;
                    const diffInHours = diffInMs / (1000 * 60 * 60);
                    return diffInHours.toFixed(2);
                },
                id: 'created',
                header: t('orders.hoursInProgress'),
                size: 200
            }
        ],
        [t],
    );

    useEffect(() => {
        getAllEntities();
    }, [])


    let inputBuffer = [];
    const ignoredKeys = ['NumLock', 'Enter'];
    useEffect(() => {
        function handleKeyDown(e) {
            if (e.key === 'Enter' && showScanPopupRef?.current) {
                const newBarcode = parseScannedInput(inputBuffer.join(''))
                setBarcode({value: newBarcode});
                inputBuffer = [];

                if (newBarcode?.startsWith('o')) {
                    chooseOrder(newBarcode);
                }
                return;
            }

            if (!ignoredKeys.includes(e.key)) {
                inputBuffer.push(e.key);
            }
        }

        document.addEventListener('keydown', handleKeyDown);

        return function cleanup() {
            document.removeEventListener('keydown', handleKeyDown);
        }
    }, []);

    async function chooseOrder(orderNumber) {
        const order = await OrderService.getOrderByOrderNumber(orderNumber);
        setOrder(order.data);
        setShowOrderList(true);
    }

    async function handleGetOrderByName() {
        const trimmedOrderNumber = orderNumber.trim();

        if (!trimmedOrderNumber) {
            alert(t('msg.enterOrderNumber'));
            return;
        }

        if (!/^\d+$/.test(trimmedOrderNumber)) {
            alert(t('msg.orderDigitsOnly'));
            return;
        }

        if (trimmedOrderNumber.length > 10) {
            alert(t('msg.orderTooLong'));
            return;
        }

        const formattedOrderNumber = `o${trimmedOrderNumber}`;

        try {
            await chooseOrder(formattedOrderNumber);
            setShowOrderNumberDialog(false);
            setOrderNumber('');
        } catch (error) {
            if (error.response?.status === 404) {
                alert(t('msg.orderNotFound', {number: trimmedOrderNumber}));
            } else if (error.response?.status === 403) {
                alert(t('msg.noAccessOrder'));
            } else {
                alert(t('msg.errorSearchingOrder', {message: error.response?.data?.message || error.message}));
            }
        }
    }

    function parseScannedInput(input) {
        const matches = input.match(/Alt(\d+)/g);
        if (!matches) return input;

        return matches.map((code) => {
            const num = code.replace("Alt", "");
            return String.fromCharCode(parseInt(num));
        }).join('');
    }

    function isOrderInRedZone(orderDateStr) {
        const RED_ZONE_ORDERS_DATE = new Date();

        const orderDate = new Date(orderDateStr);

        const diffInMs = RED_ZONE_ORDERS_DATE - orderDate;
        const diffInHours = diffInMs / (1000 * 60 * 60);

        const res = diffInHours >= 24;
        return res
    }

    async function startToCollect() {
        await store.checkEntitiesInWork();

        const userDemandsInWork = toJS(store.entitiesInWork);
        const hasCurrent = userDemandsInWork.find(item => item.current === true);
        if (hasCurrent) {
            const url = `/demand/${hasCurrent.id}`;
            navigate(url);
            return;
        }

        const newDemand = await DemandService.getNewDemand().then(data => data.data);
        if (!newDemand) {
            alert(t('msg.noShipments'))
            return;
        }

        userDemandsInWork.push(newDemand);
        store.setEntitiesInWork(userDemandsInWork);
        navigate(`/demand/${newDemand.id}`);
    }

    async function handleShowMyOrders() {
        await store.checkEntitiesInWork();
        setShowWaitingList(true);
    }

    function handleEntityRemoved() {
        setShowWaitingList(true);
    }

    function handleLogout() {
        navigate('/login')
    }

    function handleAllOrders() {
        navigate('/orders')
    }

    function handleAllOrdersInWork() {
        navigate('/ordersinwork')
    }


    async function getAllEntities() {
        try {
            let response;
            if (store.positionKey === 'assembler') {
                response = await OrderService.getAllOrders();
            } else if (store.positionKey === 'packer') {
                response = await DemandService.getAllDemands();
            } else if (store.positionKey === 'receiver') {
                response = { data: [] };
            } else {
                response = await OrderService.getAllOrders();
            }
            setEntities(response.data);
        } catch (e) {
            console.log(e);
        }
    }

    function sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    // Открыть диалог выбора тестовой отгрузки
    function openTestDialog() {
        setTestDialogOpen(true);
    }

    async function startTestDemand() {
        if (!testDemandId.trim()) {
            alert(t('msg.enterShipmentId'));
            return;
        }

        try {
            const response = await DemandService.getSpecificDemand(testDemandId);
            const testDemand = response.data.demand;

            await store.checkEntitiesInWork();
            const userDemandsInWork = toJS(store.entitiesInWork);
            userDemandsInWork.push(testDemand);
            store.setEntitiesInWork(userDemandsInWork);

            setTestDialogOpen(false);
            setTestDemandId('');

            navigate(`/demand/${testDemand.id}`);

        } catch (error) {
            if (error.response?.status === 409) {
                alert(error.response.data.message || t('msg.shipmentAlreadyInProgress'));
            } else if (error.response?.status === 404) {
                alert(t('msg.shipmentNotFound', {id: testDemandId}));
            } else {
                alert(t('msg.errorLoadingShipment', {message: error.message}));
            }
        }
    }

    return (
        <div>
            {entities
                ?
                <div style={{display: "grid", width: "200px", gridTemplateColumns: "auto"}}>
                    <h3 id={'welcomeLabel'} ref={labelRef}>{t('msg.welcome', {email: store.user.email})}</h3>
                    {store.user.email !== "admin"
                        ?
                        <div style={{display: "grid", justifyContent: "center"}}>
                            {store.positionKey === 'packer' ?
                                <>
                                    <MyButton onClick={startToCollect}>{t('btn.newOrder')}</MyButton>
                                    <MyButton onClick={() => navigate('/chestnyznak-checker')} style={{width: '180px', textAlign: 'center', marginTop: '10px'}}>
                                        🏷️ {t('btn.checkMarkingCodes')}
                                    </MyButton>
                                    {process.env.REACT_APP_ENV === 'development' && (
                                        <MyButton
                                            onClick={openTestDialog}
                                            style={{
                                                backgroundColor: '#ff9800',
                                                marginTop: '10px',
                                                color: 'white'
                                            }}
                                        >
                                            🧪 {t('btn.testShipment')}
                                        </MyButton>
                                    )}
                                </>
                                :
                                ""}

                            {store.positionKey === 'assembler' ?
                                <>
                                    <MyButton onClick={() => setShowScanPopup(true)}>{t('btn.scanOrder')}</MyButton>
                                    <MyButton onClick={() => setShowOrderNumberDialog(true)}>{t('btn.getOrderByNumber')}</MyButton>
                                    <MyButton onClick={() => navigate('/chestnyznak-checker')} style={{width: '180px', textAlign: 'center', marginTop: '10px'}}>
                                        🏷️ {t('btn.checkMarkingCodes')}
                                    </MyButton>
                                </>
                                :
                                ""}

                            {store.positionKey === 'receiver' ?
                                <>
                                    <MyButton onClick={() => navigate('/receipts')} style={{width: '180px', textAlign: 'center'}}>{t('btn.receivingModule')}</MyButton>
                                    <MyButton onClick={() => navigate('/chestnyznak-checker')} style={{width: '180px', textAlign: 'center', marginTop: '10px'}}>
                                        🏷️ {t('btn.checkMarkingCodes')}
                                    </MyButton>
                                </>
                                :
                                ""}

                            {store.positionKey !== 'receiver' && (
                                <MyButton onClick={handleShowMyOrders}>{t('btn.myOrders')}</MyButton>
                            )}
                            {store.positionKey !== 'receiver' && (
                                <div>
                                    <h3>{t('nav.currentOrders')}</h3>
                                    {entities
                                        ?
                                        <MaterialReactTable columns={store.positionKey === 'packer' ? columnsWithDemand : columns} data={entities}
                                                            initialState={{pagination}}
                                                            muiTableBodyRowProps={({row}) => ({
                                                                sx: {
                                                                    backgroundColor: isOrderInRedZone(row.original.created) ? '#f14545' : '#ffffff',
                                                                },
                                                            })}
                                                            renderTopToolbarCustomActions={() => {}}
                                        />
                                        :
                                        ""
                                    }
                                </div>
                            )}
                        </div>
                        :
                        ""

                    }
                    <Popup open={showWaitingList} onClose={() => setShowWaitingList(false)} modal> <EntityList
                        entities={store.entitiesInWork}
                        title={t('nav.ordersWaitingList')}
                        onEntityRemoved={handleEntityRemoved}/> </Popup>

                    <Popup open={showScanPopup} onClose={() => setShowScanPopup(false)} modal>
                        <h3>{t('nav.scanBarcode')}</h3>
                    </Popup>

                    <Popup open={showOrderList} onClose={() => setShowOrderList(false)} modal> <EntityList
                        entities={order}
                        title={t('nav.selectOrder')}/> </Popup>

                    {store.user.email === "admin"
                        ?
                        <div>
                            <MyButton onClick={handleAllOrders}>
                                {t('btn.allOrdersForAssembly')}
                            </MyButton>
                            <MyButton onClick={handleAllOrdersInWork}>
                                {t('btn.allOrdersInProgress')}
                            </MyButton>
                        </div>
                        :
                        ""
                    }
                    <div style={{display: 'flex', alignItems: 'center', gap: 8, marginTop: 8}}>
                        <LanguageSwitcher />
                        <MyButton onClick={async () => {
                            await store.logout();
                            handleLogout();
                        }} style={{width: '180px', textAlign: 'center'}}>
                            {t('btn.logout')}
                        </MyButton>
                    </div>
                </div>
                :
                <div></div>
            }

            {/* Dialog for entering order number */}
            <Dialog open={showOrderNumberDialog} onClose={() => {
                setShowOrderNumberDialog(false);
                setOrderNumber('');
            }}>
                <DialogTitle>{t('btn.getOrderByNumber')}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('label.orderNumber')}
                        placeholder={t('placeholder.orderNumber')}
                        fullWidth
                        variant="outlined"
                        value={orderNumber}
                        onChange={(e) => setOrderNumber(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleGetOrderByName();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setShowOrderNumberDialog(false);
                        setOrderNumber('');
                    }}>{t('btn.cancel')}</Button>
                    <Button onClick={handleGetOrderByName} variant="contained">{t('btn.getOrder')}</Button>
                </DialogActions>
            </Dialog>

            {/* Dialog for entering test shipment ID */}
            <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)}>
                <DialogTitle>{t('nav.testShipmentMarking')}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('label.shipmentId')}
                        placeholder={t('placeholder.shipmentId')}
                        fullWidth
                        variant="outlined"
                        value={testDemandId}
                        onChange={(e) => setTestDemandId(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                startTestDemand();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTestDialogOpen(false)}>{t('btn.cancel')}</Button>
                    <Button onClick={startTestDemand} variant="contained">{t('btn.openShipment')}</Button>
                </DialogActions>
            </Dialog>
        </div>
    )
});

export default Start;